<template>
  <el-form
    :model="form"
    :disabled="disabled"
    :rules="rules"
    :validateOnRuleChange="false"
    inline
    @submit.prevent.native
    ref="form"
    class="c-invitation-form"
    :class="{ 'c-invitation-form--disabled': disabled }"
  >
    <h3
      class="fnt fnt-default-light highlighted text-center c-invitation-form__title"
    >
      <img
        :src="
          invitationData.isAccepted
            ? 'img/orange_seat.png'
            : 'img/gray_seat.png'
        "
        alt=""
      />
      {{ $t("STATUS__SEAT").toUpperCase() }}&nbsp;{{ idx + 1 }}
    </h3>
    <el-form-item
      prop="recipientFirstname"
      :error="violations.recipient_firstname"
      class="block with-control-label"
    >
      <el-input
        v-model="form.recipientFirstname"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        :id="`recipientFirstname_${id}`"
      >
        <label
          :for="`recipientFirstname_${id}`"
          slot="prepend"
          :class="[isLabelActive(`recipientFirstname_${id}`) ? 'active' : '']"
          >{{ $t("STATUS__FIRSTNAME") }}<sup>*</sup></label
        >
      </el-input>
    </el-form-item>

    <el-form-item
      prop="recipientEmail"
      :error="violations.recipient_email"
      class="block with-control-label"
    >
      <el-input
        v-model="form.recipientEmail"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        :id="`recipientEmail_${id}`"
      >
        <label
          :for="`recipientEmail_${id}`"
          slot="prepend"
          :class="[isLabelActive(`recipientEmail_${id}`) ? 'active' : '']"
          >{{ $t("STATUS__EMAIL") }}<sup>*</sup></label
        >
      </el-input>
    </el-form-item>

    <el-button
      v-if="!disabled"
      type="primary"
      @click="submit"
      class="fnt w100"
      >{{ $t("STATUS__CONFIRM") }}</el-button
    >

    <div v-if="disabled" class="c-invitation-form__status">
      <div v-if="invitationData.isAccepted" class="content">
        <i class="far fa-check-circle icon icon--success"></i
        >{{ $t("STATUS__ACCEPTED") }}
      </div>
      <div v-else class="content">
        <i class="far fa-clock icon  icon--warning"></i
        >{{ $t("STATUS__EXPIRES_IN").replace("%count%", this.expiresIn) }}
      </div>
    </div>

    <confirm-email-dialog
      :dialog-visible="!!suggestedEmail"
      :suggested-email="suggestedEmail"
      :original-email="form.recipientEmail"
      @confirm="confirmEmail"
      @close="
        suggestedEmail = null
        isFormSubmitted = false
      "
    ></confirm-email-dialog>
  </el-form>
</template>

<script>
import to from "await-to-js"
import spacetime from "spacetime"

import InvitationService from "../../services/api/InvitationService"
import ConfirmEmailDialog from "../Lib/ConfirmEmailDialog"
import { getEmailSuggestions } from "../../services/mailcheck"

const invitationService = new InvitationService()

export default {
  name: "InvitationForm",
  components: {
    ConfirmEmailDialog
  },
  props: {
    invitationData: {
      type: Object,
      required: true
    },
    idx: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      id: null,
      isAccepted: null,
      expiresAt: null,
      isFormSubmitted: false,
      form: {
        recipientFirstname: null,
        recipientLastname: null,
        recipientEmail: null
      },
      violations: {},
      inFocus: null,
      token: null,
      suggestedEmail: null
    }
  },
  computed: {
    rules() {
      return {
        recipientEmail: [
          {
            required: true,
            pattern: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
            message: this.$t("SHARED__EMAIL_VALIDATION"),
            trigger: "change"
          }
        ],
        recipientFirstname: [
          {
            required: true,
            min: 2,
            message: this.$t("SHARED__MIN_LENGTH_VALIDATION").replace("%d", 2),
            trigger: "blur"
          }
        ]
      }
    },
    disabled() {
      // Existing invitation from DB
      return !!this.invitationData.createdAt
    },
    isEmpty() {
      return (
        !this.form.recipientFirstname &&
        !this.form.recipientLastname &&
        !this.form.recipientEmail
      )
    },
    expiresIn() {
      if (!this.invitationData.expiresAt) return false

      let exp = spacetime(this.invitationData.expiresAt * 1000)
      const now = spacetime.now()
      return now.diff(exp, "day")
    }
  },
  watch: {
    "form.recipientEmail"(val) {
      this.form.recipientEmail = val.trim()
    }
  },
  methods: {
    async submit() {
      if (this.disabled || this.isEmpty || this.isFormSubmitted) return

      this.violations = {}

      let invalid
      ;[invalid] = await to(this.validate())

      if (invalid) {
        return false
      }

      const suggestion = await getEmailSuggestions(this.form.recipientEmail)
      if (suggestion) {
        this.suggestedEmail = suggestion.full
      } else {
        this.sendInvitation()
      }
    },
    validate() {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate(valid => {
          if (valid) {
            resolve("yes")
          } else {
            reject("no")
          }
        })
      })
    },
    async sendInvitation() {
      this.isFormSubmitted = true
      let err, res
      ;[err, res] = await to(
        invitationService.sendInvitation(this.form, this.token)
      )

      if (err) {
        this.isFormSubmitted = false
        if (err.response.status !== 500) {
          const violations = err.response.data.violations
          violations.forEach(violation => {
            this.violations[violation.propertyPath] = violation.message
          })
          this.$forceUpdate()
        }

        return
      }

      this.id = res.id
      this.isAccepted = res.isAccepted
      this.expiresAt = res.expiresAt
      this.$emit("invitationSent")
    },
    confirmEmail(confirmedEmail) {
      this.suggestedEmail = null
      this.form.recipientEmail = confirmedEmail
      this.sendInvitation()
    },
    isLabelActive(field) {
      return this.inFocus === field || this.form[field.split("_")[0]]
    },
    handleInputFocus(evt) {
      this.inFocus = evt.target.id
    },
    handleInputBlur(evt) {
      const id = evt.target.id
      if (this.inFocus === id) {
        this.inFocus = null
      }
    }
  },
  created() {
    this.token = this.$route.query.t

    this.form.recipientFirstname = this.invitationData.recipientFirstname
    this.form.recipientLastname = this.invitationData.recipientLastname
    this.form.recipientEmail = this.invitationData.recipientEmail
    this.id = this.invitationData.id
    this.expiresAt = this.invitationData.expiresAt
    this.isAccepted = this.invitationData.isAccepted
  }
}
</script>

<style scoped></style>
